/* Copyright (c) 2020-present CloudIO Inc */

import { analyzeSelect } from 'componentregistry/components/Select/SelectAnalyze';
import { isNotEmpty } from 'componentregistry/utils/isEmpty';
import { Platform } from '../../../../platform/Platform';
import {
  AliasType,
  ComponentManager,
  ComponentManagerFn,
  ViewAttributeChooserProp,
} from '../../../../registry/Component.type';
import { ItemType } from '../../../../registry/ComponentRegistry';
import { baseComponentManager } from '../../../../registry/Components';
import { baseColumnProps, isBaseColumnPropertyVisible } from '../BaseColumn';
import { defaultValue } from './AsyncSelectColumn';
import { helpRenders } from './AsyncSelectColumn_Help';

const AsyncSelectColumnManager: ComponentManagerFn<'AsyncSelectColumn'> = (
  platform: Platform,
): ComponentManager<'AsyncSelectColumn'> => {
  return {
    ...baseComponentManager<'AsyncSelectColumn'>(platform, 'AsyncSelectColumn'),
    type: 'AsyncSelectColumn',
    name: 'Async Select Column',
    isPropertyVisible(prop, itemData, { appUid, itemId, pageId }): boolean {
      if (
        itemId &&
        !isBaseColumnPropertyVisible({ appUid, platform, itemId, pageId, prop })
      ) {
        return false;
      }

      switch (prop) {
        case 'valueAttribute':
        case 'labelAttribute':
          return isNotEmpty(itemData.comboDatasourceAlias);
        case 'specialCharacters':
          return !!itemData.ignoreSpecialCharacters;
        default:
          return true;
      }
    },
    isChildAllowed: (page, item, childType) =>
      (
        [
          'ColumnFooter',
          'SelectReturnValueMapping',
          'TriggerOnCellClick',
          'TriggerOnChange',
          'TriggerAfterServerValidate',
        ] as ItemType[]
      ).includes(childType),
    defaultValue,
    props: {
      ...baseColumnProps,
      viewAttribute: {
        ...(baseColumnProps.viewAttribute as ViewAttributeChooserProp),
        getAttributeDataTypes: () => {
          return ['String', 'Integer', 'Decimal', 'Double', 'Reference'];
        },
        category: 'Data',
        required: true,
      },
      displayAttribute: {
        label: 'Display Attribute',
        type: 'ViewAttributeChooser',
        category: 'Data',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['String', 'Integer', 'Decimal', 'Double'],
        required: true,
      },
      comboDatasourceAlias: {
        label: 'Data Store Alias',
        type: 'DataSourceAliasChooser',
        category: 'Select Options',
        required: true,
        allowedTypes: ['CloudDataStore', 'ReadOnlyDataStore'],
        helpText:
          'Choose a DataSource to be used to fetch the list of options for the Select',
      },
      valueAttribute: {
        label: 'Value Attribute',
        type: 'ComboAttributeChooser',
        aliasType: 'LOV',
        required: true,
        getAttributeDataTypes: () => 'ALL',
        category: 'Select Options',
        helpText:
          'The selected value from this attribute will be copied to the base View Attribute selected above.',
      },
      labelAttribute: {
        label: 'Label Attribute',
        type: 'ComboAttributeChooser',
        aliasType: 'LOV',
        required: true,
        getAttributeDataTypes: () => 'ALL',
        category: 'Select Options',
        helpText:
          'The selected value from this attribute will be copied to the base Display Attribute selected above.',
      },
      minFilterChars: {
        label: 'Minimum Filter Characters',
        type: 'number',
        category: 'Select Options',
        helpText: 'Minimum number of characters to be entered before querying',
      },
      placeholder: {
        label: 'Placeholder',
        type: 'string',
      },
      unknownValueMessage: {
        label: 'Unknown Value Message',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'Unknown Value Error Message',
      },
      selectOptionOnBlur: {
        label: 'Select Option On Blur',
        type: 'boolean',
        category: 'Select Options',
        helpText: 'Enable the option to select the topmost option on Blur.',
      },
      selectOptionOnTab: {
        label: 'Select Option On Tab',
        type: 'boolean',
        category: 'Select Options',
        helpText: 'Enable the option to select the topmost option on Tab.',
      },
      serverValidateOnChange: {
        label: 'Server Validate On Change',
        type: 'boolean',
        category: 'Data',
      },
      ignoreSpecialCharacters: {
        label: 'Ignore Special Characters',
        type: 'boolean',
        helpText: 'Enable this to ignore special characters while searching.',
        category: 'Data',
      },
      specialCharacters: {
        label: 'Special Characters',
        type: 'string',
        helpText:
          'Enter special characters to exclude during the search. Use a semicolon (;) as the delimiter.',
        category: 'Data',
      },
    },
    generateShortId: (item) =>
      typeof item.label === 'string' ? item.label : item.label?.value,
    getDataSourceAliasProperty: (aliasType: AliasType) =>
      aliasType === 'LOV' ? { aliasProp: 'comboDatasourceAlias' } : undefined,
    isDataSourceAliasUsed: (item, alias) =>
      item.props.comboDatasourceAlias === alias,
    getDisplayText: (item) =>
      typeof item.props.label === 'string'
        ? item.props.label
        : item.props.label.value ?? '',
    category: 'Data Grid',
    icon: { iconName: 'io-select', iconType: 'cloudio' },
    excludeChildren: true,
    replaceableWith: ['LookupSelectColumn', 'SelectColumn', 'StringColumn'],
    analyze: analyzeSelect,
    helpRenders,
    description: `**Overview**<br/>
The Async Select Column is a high-performance dropdown component designed for Data Grids. Unlike a standard Select Column (which loads all options into memory at once), this component fetches options dynamically from the server as the user types.
Use this component when your dropdown list is too large to load entirely (e.g., "Search Products" with 50,000+ items, or "Assign User" in a large organization). It behaves like a "Search-as-you-type" input (Autocomplete) directly inside the grid cell.

**1. The "Two-Sided" Data Binding**<br/>
In a Data Grid, efficiency is key. To ensure the grid renders fast without looking up labels for every row, this component requires you to bind two attributes in your main Data Store: one for the Value (ID) and one for the Display Text (Label).
* **View Attribute (The ID):** The attribute where the selected Value is stored (e.g., "product_id"). This is what gets sent to the database.
* **Display Attribute (The Label):** The attribute where the selected Label is stored (e.g., "product_name").
* **Why is this required?** When the user isn't editing the row, the grid displays this text directly. This prevents the grid from needing to query the database just to render the text "iPhone 15" instead of "10923".



**2. Configuring the Data Source (LOV)**<br/>
You must define where the dropdown options come from.
* **Data Store Alias:** Select the external Data Store (e.g., "productsMasterList") that contains the list of options.
* **Value Attribute:** The field in the external store to use as the underlying ID (e.g., "sku_code").
* **Label Attribute:** The field in the external store to show in the list (e.g., "item_description").

**3. Performance & Filtering**<br/>
Since this component queries the server repeatedly, you can tune its behavior to reduce network traffic.
* **Minimum Filter Characters:** The number of characters a user must type before the query triggers. (Recommendation: Set to 3 for very large datasets to prevent searching for "A" or "Th").
* **Unknown Value Message:** Custom text to display if the ID stored in the row doesn't match any option found on the server (e.g., "Archived Product").

**4. User Experience (UX)**<br/>
* **Select Option On Blur / Tab:**
    * **Enabled:** If the user types "Appl" and hits Tab (or clicks away), the system automatically selects the first matching option ("Apple"). This speeds up data entry for power users.
    * **Disabled:** The user must explicitly click or press Enter to select an option.

**5. Advanced Logic**<br/>
* **Select Return Value Mapping:** This allows you to copy extra data from the selected option into other columns in the same row.
    * **Child Component:** Add a **SelectReturnValueMapping** child.
    * **Example:** When the user selects a "Product," automatically populate the "Unit Price" and "Tax Rate" columns in the same grid row.`,
  };
};

export default AsyncSelectColumnManager;
